import React, { useState, useEffect } from "react";
import { DataTable } from "@/components/ui/data-table";
import { CouponTableSkeleton } from "@/components/ui/coupon-table-skeleton";
import { Button } from "@/components/ui/button";
import { Plus, Eye, Edit } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import countries from "@/constants/array/country_city.json";
import { OutletApi } from "@/api/giftcard.api";
import { HandleError } from "@/api/axiosClient";

interface Outlet {
  id: string;
  outletName: string;
  address: string;
  city: string;
  country: string;
  state: string;
  postalCode: string;
  phone: string;
  email: string;
}

// Form schema
const outletFormSchema = z.object({
  outletName: z.string().min(1, { message: "Outlet name is required" }),
  address: z.string().min(1, { message: "Address is required" }),
  city: z.string().min(1, { message: "City is required" }),
  country: z.string().min(1, { message: "Country is required" }),
  state: z.string().min(1, { message: "State is required" }),
  postalCode: z.string().min(1, { message: "Postal code is required" }),
  phone: z.string().min(1, { message: "Phone number is required" }),
  email: z.string().email({ message: "Invalid email address" }),
});

type OutletFormValues = z.infer<typeof outletFormSchema>;

export default function Outlets() {
  const [outlets, setOutlets] = useState<Outlet[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedOutlet, setSelectedOutlet] = useState<Outlet | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<any>(null);
  const { toast } = useToast();

  // Fetch outlets from API
  useEffect(() => {
    fetchOutlets();
  }, []);

  const fetchOutlets = async () => {
    setIsLoading(true);
    try {
      const response = await OutletApi.getAllOutlets();
      
      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Error",
          description: response.error_msg,
          variant: "destructive",
        });
      } else {
        // Map API response to Outlet interface
        const mappedOutlets: Outlet[] = (response.getOutletList || []).map(
          (outlet) => ({
            id: String(outlet.id),
            outletName: outlet.name,
            address: outlet.address,
            city: outlet.city,
            country: outlet.country,
            state: outlet.state,
            postalCode: outlet.postalCode,
            phone: outlet.phone,
            email: outlet.email,
          })
        );
        setOutlets(mappedOutlets);
      }
    } catch (error: any) {
      console.error("Error fetching outlets:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to fetch outlets",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const form = useForm<OutletFormValues>({
    resolver: zodResolver(outletFormSchema),
    defaultValues: {
      outletName: "",
      address: "",
      city: "",
      country: "",
      state: "",
      postalCode: "",
      phone: "",
      email: "",
    },
  });

  const onSubmit = async (data: OutletFormValues) => {
    try {
      // Extract country name from selected country object
      const countryName = selectedCountry?.name || data.country;

      // Use outlet id if editing, otherwise 0 for new creation
      const outletId = selectedOutlet ? Number(selectedOutlet.id) : 0;

      const payload = {
        p_Id: outletId,
        p_Name: data.outletName,
        p_Address: data.address,
        p_City: data.city,
        p_State: data.state,
        p_Country: countryName,
        p_PostalCode: data.postalCode,
        p_Phone: data.phone,
        p_Email: data.email,
      };

      const response = await OutletApi.createUpdateOutlet(payload);

      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Error",
          description: response.error_msg,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: response.message || (outletId > 0 ? "Outlet updated successfully" : "Outlet added successfully"),
        });

        // Refresh outlets list after adding/updating
        await fetchOutlets();

        form.reset();
        setSelectedCountry(null);
        setSelectedOutlet(null);
        setIsAddModalOpen(false);
        setIsEditModalOpen(false);
      }
    } catch (error: any) {
      console.error("Error creating/updating outlet:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to save outlet",
        variant: "destructive",
      });
    }
  };

  const handleCancel = () => {
    form.reset();
    setSelectedCountry(null);
    setSelectedOutlet(null);
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
  };

  const handleView = (outlet: Outlet) => {
    setSelectedOutlet(outlet);
    setIsViewModalOpen(true);
  };

  const handleEdit = (outlet: Outlet) => {
    setSelectedOutlet(outlet);
    // Find country object from countries list
    const countryObj = (countries as any)?.find(
      (c: any) => c.name === outlet.country
    );
    
    // Pre-fill form with outlet data
    form.reset({
      outletName: outlet.outletName,
      address: outlet.address,
      city: outlet.city,
      country: countryObj ? JSON.stringify(countryObj) : outlet.country,
      state: outlet.state,
      postalCode: outlet.postalCode,
      phone: outlet.phone,
      email: outlet.email,
    });
    
    setSelectedCountry(countryObj || null);
    setIsViewModalOpen(false);
    setIsEditModalOpen(true);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Outlets</h1>
        <p className="text-muted-foreground">
          Manage and track all your outlets
        </p>
      </div>

      {isLoading ? (
        <CouponTableSkeleton rowCount={5} />
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-end">
            <Button
              className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
              onClick={() => {
                setSelectedOutlet(null);
                setIsAddModalOpen(true);
              }}
            >
              <Plus className="h-4 w-4" />
              Add Outlet
            </Button>
          </div>
          <DataTable
            title=""
            data={outlets}
            cellClassName="py-6 text-gray-700"
            columns={[
              {
                header: "Outlet Name",
                accessorKey: "outletName" as keyof Outlet,
              },
              {
                header: "Address",
                accessorKey: "address" as keyof Outlet,
              },
              {
                header: "City",
                accessorKey: "city" as keyof Outlet,
              },
              {
                header: "Country",
                accessorKey: "country" as keyof Outlet,
              },
              {
                header: "State",
                accessorKey: "state" as keyof Outlet,
              },
              {
                header: "Postal Code",
                accessorKey: "postalCode" as keyof Outlet,
              },
              {
                header: "Phone",
                accessorKey: "phone" as keyof Outlet,
              },
              {
                header: "Email",
                accessorKey: "email" as keyof Outlet,
              },
              {
                header: "Action",
                id: "action",
                cell: (row: Outlet) => (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleView(row)}
                    className="flex items-center gap-2"
                  >
                    <Eye className="h-4 w-4" />
                    View
                  </Button>
                ),
              },
            ]}
          />
        </div>
      )}

      {/* View Outlet Modal */}
      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Outlet Details</DialogTitle>
          </DialogHeader>
          {selectedOutlet && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Outlet Name</label>
                  <p className="text-sm text-gray-900 mt-1">{selectedOutlet.outletName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Address</label>
                  <p className="text-sm text-gray-900 mt-1">{selectedOutlet.address}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">City</label>
                  <p className="text-sm text-gray-900 mt-1">{selectedOutlet.city}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Country</label>
                  <p className="text-sm text-gray-900 mt-1">{selectedOutlet.country}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">State</label>
                  <p className="text-sm text-gray-900 mt-1">{selectedOutlet.state}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Postal Code</label>
                  <p className="text-sm text-gray-900 mt-1">{selectedOutlet.postalCode}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Phone</label>
                  <p className="text-sm text-gray-900 mt-1">{selectedOutlet.phone}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Email</label>
                  <p className="text-sm text-gray-900 mt-1">{selectedOutlet.email}</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsViewModalOpen(false)}
            >
              Close
            </Button>
            {selectedOutlet && (
              <Button
                type="button"
                className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
                onClick={() => handleEdit(selectedOutlet)}
              >
                <Edit className="h-4 w-4" />
                Edit
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Outlet Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Outlet</DialogTitle>
            <DialogDescription>
              Fill in the details to create a new outlet
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {/* Outlet Name */}
              <FormField
                control={form.control}
                name="outletName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Outlet Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter outlet name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Address */}
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter address" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* City */}
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City *</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter city" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Country */}
                <FormField
                  control={form.control}
                  name="country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country *</FormLabel>
                      <Select
                        onValueChange={(value) => {
                          const selected = JSON.parse(value);
                          setSelectedCountry(selected);
                          field.onChange(value);
                        }}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {(countries as any)?.map((country: any) => (
                            <SelectItem
                              key={country?.name}
                              value={JSON.stringify(country)}
                            >
                              {country?.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* State */}
                <FormField
                  control={form.control}
                  name="state"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>State *</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter state" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Postal Code */}
                <FormField
                  control={form.control}
                  name="postalCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Postal Code *</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter postal code" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Phone */}
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone *</FormLabel>
                      <FormControl>
                        <Input
                          type="tel"
                          placeholder="Enter phone number"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Email */}
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email *</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="Enter email address"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700">
                  Add Outlet
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Edit Outlet Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Outlet</DialogTitle>
            <DialogDescription>
              Update the outlet details below
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {/* Outlet Name */}
              <FormField
                control={form.control}
                name="outletName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Outlet Name *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter outlet name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Address */}
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Address *</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter address" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* City */}
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City *</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter city" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Country */}
                <FormField
                  control={form.control}
                  name="country"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Country *</FormLabel>
                      <Select
                        onValueChange={(value) => {
                          const selected = JSON.parse(value);
                          setSelectedCountry(selected);
                          field.onChange(value);
                        }}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {(countries as any)?.map((country: any) => (
                            <SelectItem
                              key={country?.name}
                              value={JSON.stringify(country)}
                            >
                              {country?.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* State */}
                <FormField
                  control={form.control}
                  name="state"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>State *</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter state" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Postal Code */}
                <FormField
                  control={form.control}
                  name="postalCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Postal Code *</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter postal code" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Phone */}
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone *</FormLabel>
                      <FormControl>
                        <Input
                          type="tel"
                          placeholder="Enter phone number"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Email */}
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email *</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="Enter email address"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700">
                  Update Outlet
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
